/**
 * useAvailableSlots Hook
 * 
 * Fetches available slots from the scheduling-availability edge function.
 * Provides loading, error, and refetch capabilities.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import type { AvailableSlot } from '@/lib/scheduling';
import { transformSlots } from './availableSlotsLogic';

interface UseAvailableSlotsOptions {
  childId?: string;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

interface UseAvailableSlotsResult {
  slots: AvailableSlot[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAvailableSlots(options: UseAvailableSlotsOptions = {}): UseAvailableSlotsResult {
  const { childId, startDate, endDate, enabled = true } = options;
  
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invokeWithToken = useCallback(async (accessToken: string) => {
    return supabase.functions.invoke('scheduling-availability', {
      body: { childId, startDate, endDate },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }, [childId, startDate, endDate]);

  const fetchSlots = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      // getSession() checks expiry and auto-refreshes via refresh token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('Please sign in to view available slots');
        setLoading(false);
        return;
      }

      let { data, error: fnError } = await invokeWithToken(session.access_token);

      // On 401, the cached token was stale despite getSession() — force refresh and retry once
      if (fnError && (fnError as any).name === 'FunctionsHttpError') {
        const ctx = (fnError as any).context;
        if (ctx instanceof Response && ctx.status === 401) {
          const { data: { session: refreshed } } = await supabase.auth.refreshSession();
          if (!refreshed?.access_token) {
            setError('Session expired — please sign in again');
            setLoading(false);
            return;
          }
          ({ data, error: fnError } = await invokeWithToken(refreshed.access_token));
        }
      }

      // Handle remaining errors after possible retry
      if (fnError) {
        const errorName = (fnError as any).name || 'UnknownError';
        if (errorName === 'FunctionsHttpError') {
          const ctx = (fnError as any).context;
          if (ctx instanceof Response) {
            const body = await ctx.json().catch(() => ({}));
            throw new Error(body?.error || `Server error (${ctx.status})`);
          }
        }
        throw new Error(fnError.message || 'Failed to fetch slots');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch slots');
      }

      const transformedSlots: AvailableSlot[] = transformSlots(data.slots || []);

      setSlots(transformedSlots);
    } catch (err) {
      console.error('[useAvailableSlots] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch slots');
    }
    setLoading(false);
  }, [enabled, invokeWithToken]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  return {
    slots,
    loading,
    error,
    refetch: fetchSlots,
  };
}
